import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../../layout/Icon.jsx';
import { api, apiError } from '../../../shared/api.js';
import { formatIQD } from '../../../shared/format.js';
import { printReceipt } from '../../../shared/printReceipt.js';
import { toast } from '../../../shared/useToast.js';
import { useAuth } from '../../../shared/useAuth.js';
import {
  useShiftUI,
  useCurrentShift,
  useOpenShift,
  useCloseShift,
  useShiftReport
} from '../../../shared/useShift.js';
import { ShiftReceipt } from './ShiftReceipt.jsx';

function Modal({ title, sub, onClose, children, footer, width = 460 }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(15,16,18,0.5)' }}>
      <div className="bg-white rounded-[14px] shadow-card w-full overflow-hidden max-h-[92vh] flex flex-col" style={{ maxWidth: width }}>
        <div className="card-h">
          <div className="flex-1">
            <h3 className="text-[15px] font-bold m-0">{title}</h3>
            {sub ? <div className="text-[11.5px] text-ink-3 mt-0.5">{sub}</div> : null}
          </div>
          <button type="button" className="text-ink-3 hover:text-ink" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">{children}</div>
        {footer ? <div className="card-h border-b-0 border-t flex gap-2">{footer}</div> : null}
      </div>
    </div>
  );
}

function useCashboxes() {
  return useQuery({
    queryKey: ['cashboxes', 'shifts'],
    queryFn: async () => {
      const { data } = await api.get('/cashbox');
      const list = data?.cashboxes || data?.data || data || [];
      return (Array.isArray(list) ? list : []).filter((c) => c.isActive !== false);
    },
    staleTime: 15_000
  });
}

const idOf = (c) => String(c?._id || c?.id || c || '');

function MoneyInput({ value, onChange, autoFocus, big }) {
  return (
    <input
      type="number"
      min="0"
      className="input"
      style={big ? { fontSize: 20, fontWeight: 800 } : undefined}
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value === '' ? '' : Math.max(0, Math.round(Number(e.target.value) || 0)))}
      placeholder="0"
    />
  );
}

// ─────────────────────────────────────────────────────────────
function OpenShiftModal({ onClose }) {
  const me = useAuth((s) => s.user);
  const assigned = idOf(me?.assignedCashboxId);
  const { data: boxes = [] } = useCashboxes();
  const openMut = useOpenShift();

  const [cashboxId, setCashboxId] = useState('');
  const [drawerCount, setDrawerCount] = useState(0);
  const [floatAmount, setFloatAmount] = useState(0);
  const [floatSource, setFloatSource] = useState('');
  const [notes, setNotes] = useState('');

  const main = boxes.find((b) => b.isMain);
  useEffect(() => {
    if (!boxes.length) return;
    if (!cashboxId) {
      const pref = boxes.find((b) => idOf(b) === assigned) || boxes.find((b) => !b.isMain) || boxes[0];
      setCashboxId(idOf(pref));
    }
    if (!floatSource && main) setFloatSource(idOf(main));
  }, [boxes]); // eslint-disable-line react-hooks/exhaustive-deps

  const drawer = boxes.find((b) => idOf(b) === cashboxId);
  const drawerIsSource = floatSource && floatSource === cashboxId;
  const source = boxes.find((b) => idOf(b) === floatSource);
  const insufficient = Number(floatAmount || 0) > 0 && source && !drawerIsSource
    && Number(source.currentBalance || 0) < Number(floatAmount || 0);
  const total = Number(drawerCount || 0) + Number(floatAmount || 0);

  async function submit() {
    const res = await openMut.mutateAsync({
      cashboxId,
      drawerCount: Number(drawerCount || 0),
      floatAmount: Number(floatAmount || 0),
      floatSourceCashboxId: Number(floatAmount || 0) > 0 && !drawerIsSource ? floatSource : null,
      notes
    });
    if (res?.shift?.openingCarry?.mismatch) {
      toast.warning('⚠️ عدم مطابقة افتتاح الشفت! المبلغ الموجود في الدرج يختلف عمّا تركه الشفت السابق — سُجِّل الفرق للمدير.');
    }
    onClose();
  }

  return (
    <Modal
      title="فتح شفت جديد"
      sub="سيُربط بحسابك وبالقاصة (الدرج) المختارة"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إلغاء</button>
          <button
            type="button"
            className="btn btn-primary flex-1 justify-center"
            onClick={submit}
            disabled={!cashboxId || insufficient || openMut.isPending}
          >
            <Icon name="check" size={14} /> {openMut.isPending ? 'جاري الفتح...' : `فتح الشفت (${formatIQD(total)})`}
          </button>
        </>
      )}
    >
      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">🏪 القاصة (الدرج) <span className="text-red">*</span></label>
        <select className="input" value={cashboxId} onChange={(e) => setCashboxId(e.target.value)}>
          <option value="">اختر القاصة…</option>
          {boxes.map((b) => (
            <option key={idOf(b)} value={idOf(b)}>
              {b.name}{idOf(b) === assigned ? ' ★' : ''}
            </option>
          ))}
        </select>
        <div className="text-[11px] text-ink-3 mt-1">كل نقد تستلمه خلال الشفت يدخل هذه القاصة تلقائياً.</div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">المبلغ الموجود في الدرج الآن (قبل العهدة)</label>
        <MoneyInput value={drawerCount} onChange={setDrawerCount} />
        <div className="text-[11px] text-ink-3 mt-1">
          عُدّ ما في الدرج فعلاً — إن كان الشفت السابق ترك نقداً «مدوّراً» فاكتب ما وجدته، وإلا اتركه صفراً.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">العهدة (فكّة) المسحوبة</label>
          <MoneyInput value={floatAmount} onChange={setFloatAmount} big />
        </div>
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">تُسحب من</label>
          <select className="input" value={floatSource} onChange={(e) => setFloatSource(e.target.value)}>
            {boxes.filter((b) => idOf(b) !== cashboxId).map((b) => (
              <option key={idOf(b)} value={idOf(b)}>{b.name} — {formatIQD(b.currentBalance || 0)}</option>
            ))}
          </select>
        </div>
      </div>
      {insufficient ? (
        <div className="text-[11.5px] text-red font-semibold">رصيد «{source?.name}» غير كافٍ لسحب العهدة.</div>
      ) : null}

      <div className="rounded-[10px] bg-paper-2 p-2.5 text-[12.5px] flex justify-between">
        <span className="text-ink-3">إجمالي العهدة في درج {drawer?.name || '—'}</span>
        <b>{formatIQD(total)}</b>
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات (اختياري)</label>
        <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
function CloseShiftModal({ shiftId, onClose, onDone }) {
  const { data: cur } = useCurrentShift();
  const targetId = shiftId || cur?.shift?._id;
  const reportQ = useShiftReport(targetId, !!targetId);
  const shift = reportQ.data?.shift || null;
  const { data: boxes = [] } = useCashboxes();
  const closeMut = useCloseShift();

  const [counted, setCounted] = useState('');
  const [carried, setCarried] = useState(0);
  const [depositId, setDepositId] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (depositId || !boxes.length || !shift) return;
    const main = boxes.find((b) => b.isMain && idOf(b) !== idOf(shift.cashboxId));
    if (main) setDepositId(idOf(main));
  }, [boxes, shift]); // eslint-disable-line react-hooks/exhaustive-deps

  const hidden = !!shift?.summaryHidden;
  const expected = shift?.expectedCash;
  const countedNum = counted === '' ? null : Number(counted);
  const liveVariance = !hidden && countedNum != null && expected != null ? countedNum - expected : null;
  const carriedBad = countedNum != null && Number(carried || 0) > countedNum;
  const deposit = countedNum != null ? Math.max(0, countedNum - Number(carried || 0)) : 0;
  const s = shift?.summary || {};

  async function doClose(confirmZeroCash = false) {
    try {
      const res = await closeMut.mutateAsync({
        id: targetId,
        body: {
          countedCash: countedNum,
          carriedCash: Number(carried || 0),
          depositCashboxId: depositId || null,
          notes,
          confirmZeroCash
        }
      });
      toast.success('تم إقفال الشفت');
      onDone(res);
    } catch (err) {
      if (err?.response?.data?.error?.code === 'ZERO_CASH_NOT_CONFIRMED') {
        // eslint-disable-next-line no-alert
        if (window.confirm(`${apiError(err)}\n\nهل الصندوق فارغ فعلاً؟ سيُسجَّل عجز بكامل المبلغ المتوقع.`)) {
          await doClose(true);
        }
        return;
      }
      toast.error(apiError(err));
    }
  }

  return (
    <Modal
      title="إقفال الشفت"
      sub={shift ? `${shift.cashierName} • ${shift.cashboxName} • عهدة: ${formatIQD(shift.openingBalance || 0)}` : 'جاري التحميل...'}
      onClose={onClose}
      width={500}
      footer={(
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إلغاء</button>
          <button
            type="button"
            className="btn btn-success flex-1 justify-center"
            onClick={() => doClose(false)}
            disabled={!shift || countedNum == null || carriedBad || closeMut.isPending}
          >
            <Icon name="check" size={14} />
            {closeMut.isPending ? 'جاري الإقفال...' : `إقفال وترحيل (${formatIQD(deposit)})`}
          </button>
        </>
      )}
    >
      {!shift ? (
        <div className="text-center text-ink-3 py-6">جاري تحميل خلاصة الشفت...</div>
      ) : (
        <>
          {!hidden ? (
            <div className="rounded-[10px] border border-line p-2.5 text-[12.5px] space-y-1">
              <div className="font-bold mb-1">خلاصة الشفت</div>
              <div className="flex justify-between"><span className="text-ink-3">عدد الفواتير</span><b>{s.invoicesCount || 0}</b></div>
              <div className="flex justify-between"><span className="text-ink-3">نقد وارد</span><b className="text-green">{formatIQD(s.cashIn || 0)}</b></div>
              <div className="flex justify-between"><span className="text-ink-3">نقد صادر</span><b className="text-red">{formatIQD(s.cashOut || 0)}</b></div>
              <div className="flex justify-between border-t border-line pt-1">
                <span className="font-bold">النقد المتوقع في الدرج</span>
                <b className="text-orange-deep">{formatIQD(expected || 0)}</b>
              </div>
            </div>
          ) : (
            <div className="rounded-[10px] bg-paper-2 p-2.5 text-[12px] text-ink-2">
              العدّ الأعمى مُفعّل — عُدّ النقد دون معرفة المتوقع.
            </div>
          )}

          <div className="rounded-[10px] p-2.5 text-[12px]" style={{ background: '#FFF7ED', color: '#9A3412' }}>
            عُدّ النقد في الصندوق بدقّة قبل الإقفال.
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">النقد المَعدود في الدرج <span className="text-red">*</span></label>
            <MoneyInput value={counted} onChange={setCounted} autoFocus big />
            {liveVariance != null ? (
              <div className="text-[12px] mt-1 font-semibold" style={{ color: liveVariance === 0 ? '#15803D' : liveVariance > 0 ? '#1D4ED8' : '#B91C1C' }}>
                {liveVariance === 0 ? 'مطابق للمتوقع ✓' : liveVariance > 0 ? `زيادة ${formatIQD(liveVariance)}` : `عجز ${formatIQD(-liveVariance)}`}
              </div>
            ) : null}
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">🔄 النقد المدوّر للشفت التالي (يبقى في الدرج — اختياري)</label>
            <MoneyInput value={carried} onChange={setCarried} />
            {carriedBad ? (
              <div className="text-[11.5px] text-red mt-1">النقد المدوّر لا يمكن أن يتجاوز النقد المعدود.</div>
            ) : (
              <div className="text-[11px] text-ink-3 mt-1">
                فكّة تتركها للكاشير التالي — سيعدّها عند فتح شفته، وأي اختلاف يظهر «عدم مطابقة».
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">ترحيل المبلغ إلى قاصة</label>
            <select className="input" value={depositId} onChange={(e) => setDepositId(e.target.value)}>
              <option value="">— لا ترحيل (يبقى في الدرج) —</option>
              {boxes.filter((b) => idOf(b) !== idOf(shift.cashboxId)).map((b) => (
                <option key={idOf(b)} value={idOf(b)}>{b.name} — {formatIQD(b.currentBalance || 0)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات الإقفال (اختياري)</label>
            <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────
function ReportModal({ shiftId, preloaded, onClose }) {
  const q = useShiftReport(shiftId, !preloaded && !!shiftId);
  const data = preloaded || q.data;
  return (
    <Modal
      title="تقرير الشفت"
      sub={data?.shift?.status === 'open' ? 'الشفت ما زال مفتوحاً — الأرقام حيّة' : 'تقرير الإقفال'}
      onClose={onClose}
      width={380}
      footer={(
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إغلاق</button>
          <button type="button" className="btn btn-primary flex-1 justify-center" onClick={() => printReceipt()} disabled={!data}>
            <Icon name="print" size={14} /> طباعة 80مم
          </button>
        </>
      )}
    >
      <div className="grid place-items-center" style={{ background: '#FAF6EE', padding: 12, borderRadius: 10 }}>
        {data ? <ShiftReceipt shift={data.shift} movements={data.movements} /> : <div className="py-8 text-ink-3">جاري التحميل...</div>}
      </div>
    </Modal>
  );
}

/** يُركَّب مرة واحدة في الإطار العام (Shell). */
export function ShiftModals() {
  const modal = useShiftUI((s) => s.modal);
  const reportShiftId = useShiftUI((s) => s.reportShiftId);
  const closeShiftId = useShiftUI((s) => s.closeShiftId);
  const closeModal = useShiftUI((s) => s.closeModal);
  // بعد الإقفال نعرض تقرير الإقفال للطباعة مباشرة (كنظام المطاعم)
  const [closedReport, setClosedReport] = useState(null);

  if (closedReport) {
    return <ReportModal preloaded={closedReport} onClose={() => setClosedReport(null)} />;
  }
  if (modal === 'open') return <OpenShiftModal onClose={closeModal} />;
  if (modal === 'close') {
    return (
      <CloseShiftModal
        shiftId={closeShiftId}
        onClose={closeModal}
        onDone={(res) => { closeModal(); setClosedReport(res); }}
      />
    );
  }
  if (modal === 'report') {
    return <ReportModal shiftId={reportShiftId} onClose={closeModal} />;
  }
  return null;
}
